import { Box, Card, Divider, Grid, Typography } from "@mui/joy";
import { ArrowLeft, UserRoundPlus, Store } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Button, TextField, Alert, Autocomplete } from "../../../../shared";
import { newUserValidation } from "../../../../libs/form-validations";
import { useUser, useRoles } from "../../../../hooks/query";
import { Breadcrumb } from "../../../../components";
import { useApiResponseParser } from "../../../../hooks";

export default function NewUser() {
    const navigate = useNavigate();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useCreateCognito } = useUser();
    const { useList } = useRoles();
    const createCognitoSvc = useCreateCognito();
    const listRolesSvc = useList();

    const rolesData = listRolesSvc.data || [];

    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            FirstName: "",
            LastName: "",
            Email: "",
            Password: "",
            RoleId: "",
        },
        resolver: newUserValidation,
    });

    const {
        handleSubmit,
        formState: { isValid },
    } = methods;

    const registerUser = async (data: any) => {
        try {
            await createCognitoSvc.mutateAsync(data);
            navigate(-1);
        } catch (e) {
            console.error("Failed to register user:", e);
        }
    };

    return (
        <Box flex={1}>
            <Breadcrumb />
            <Divider sx={{ mb: 2 }} />
            <Box ml={3} maxWidth={700}>
                <Box>
                    <Box display={"flex"} gap={0.5} alignItems={"center"} mb={1}>
                        <Card sx={{ p: 0.5, borderRadius: "6px" }}>
                            <Store size={14} />
                        </Card>
                        <Typography level="title-lg">New User</Typography>
                    </Box>
                    <Typography level="body-sm">
                        Provide the user's details to onboard them into the system via AWS Cognito. They will receive an
                        email with their account information.
                    </Typography>
                </Box>
                <Divider sx={{ mt: 2 }} />
            </Box>

            <Box mt={2} ml={3} maxWidth={700}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(registerUser)}>
                        <Grid container spacing={2}>
                            <Grid xs={6}>
                                <TextField
                                    name="FirstName"
                                    label="First Name"
                                    helperText="Enter the first name of the user."
                                />
                            </Grid>
                            <Grid xs={6}>
                                <TextField
                                    name="LastName"
                                    label="Last Name"
                                    helperText="Enter the last name of the user."
                                />
                            </Grid>

                            <Grid xs={12}>
                                <TextField
                                    name="Email"
                                    label="Email"
                                    type="email"
                                    helperText="Provide a valid email for primary communication and login."
                                />
                            </Grid>

                            <Grid xs={12}>
                                <TextField
                                    name="Password"
                                    label="Password (Optional)"
                                    type="password"
                                    helperText="If left empty, Cognito will generate a temporary password."
                                />
                            </Grid>

                            <Grid xs={12}>
                                <Autocomplete
                                    name="RoleId"
                                    label="Role (Optional)"
                                    placeholder="Select a role"
                                    options={
                                        rolesData.map((role: any) => ({
                                            label: role.Name,
                                            value: role.Id,
                                        })) || []
                                    }
                                    getOptionLabel={(option: any) => option.label}
                                    isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
                                    value={
                                        rolesData
                                            .map((role: any) => ({
                                                label: role.Name,
                                                value: role.Id,
                                            }))
                                            .find((opt: any) => opt.value === methods.watch("RoleId")) || null
                                    }
                                    onChange={(e: any, value: any) => {
                                        methods.setValue("RoleId", value?.value || "", { shouldDirty: true, shouldValidate: true });
                                    }}
                                    helperText="Assign a primary role to the user for access control."
                                    loading={listRolesSvc.isLoading}
                                />
                            </Grid>
                        </Grid>

                        <Grid xs={12}>
                            <Typography sx={{ mt: 2 }} level="body-sm">
                                Ensure all required fields are completed accurately before proceeding.
                            </Typography>
                            <Box mt={1} display={"flex"} alignItems={"center"} gap={2}>
                                <Button
                                    variant={"soft"}
                                    startDecorator={<ArrowLeft size={18} />}
                                    color={"neutral"}
                                    onClick={() => navigate(-1)}
                                    disabled={createCognitoSvc.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant={"soft"}
                                    startDecorator={<UserRoundPlus size={18} />}
                                    loading={createCognitoSvc.isPending}
                                    loadingPosition="start"
                                    type={"submit"}
                                    disabled={!isValid || createCognitoSvc.isPending}
                                >
                                    {createCognitoSvc.isPending ? "Creating..." : "Add User"}
                                </Button>
                            </Box>
                        </Grid>
                        {createCognitoSvc.isError && (
                            <Box mt={2}>
                                <Alert errorMessage={parseErrorResponseData(createCognitoSvc.error)} />
                            </Box>
                        )}
                    </form>
                </FormProvider>
            </Box>
        </Box>
    );
}
